import React from 'react';
import classNames from 'classnames';

import Logo from '../Logo/Logo';
// Single source of truth for the hero photo — same asset the homepage hero
// renders (see HomePage/Hero). Do not vendor a copy here.
import { HERO_IMAGE } from '../../containers/HomePage/homeContent';

import css from './CrownAuthShell.module.css';

/* Mobile-band trust line: horizontal, gold middots BETWEEN items only —
   same treatment as the homepage hero trust line. Below the one-line
   breakpoint the dot before the last item is hidden (CSS) so wrapped
   lines never start or end with a dot. */
const TrustLineHorizontal = props => {
  const { items, className } = props;
  return items?.length ? (
    <div className={className}>
      {items.map((item, i) => (
        <React.Fragment key={i}>
          {i > 0 ? (
            <span className={css.trustDot} aria-hidden="true">
              ·
            </span>
          ) : null}
          <span className={css.trustItem}>{item}</span>
        </React.Fragment>
      ))}
    </div>
  ) : null;
};

/* Rail trust list: stacked rows, each with a leading gold dot. */
const TrustListStacked = props => {
  const { items } = props;
  return items?.length ? (
    <ul className={css.trust}>
      {items.map((item, i) => (
        <li key={i} className={css.trustRow}>
          <span className={css.trustLeadDot} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  ) : null;
};

/**
 * Crown auth shell (design C3, approved 2026-06-12 — see design-reference/auth-pages.html;
 * B3 backdrop + rail fixes from the 2026-06-12 operator review).
 *
 * Stage renders the homepage hero photo full-bleed under a maroon scrim
 * (deep warm cream + radial tint as the no-image fallback), with the crown
 * card centered on top: maroon rail on the left (site logo, eyebrow, headline
 * with optional italic em-line, stacked trust list) and a body slot for the
 * page content. Below 768px the rail becomes a top band and the trust line
 * moves below the body as a horizontal row.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.rootClassName - Overrides css.stage
 * @param {string?} props.className - Extends the root class
 * @param {ReactNode} props.eyebrow - Small gold uppercase line above the headline
 * @param {ReactNode} props.headline - Rail headline (rendered as the page h1)
 * @param {ReactNode?} props.headlineEm - Optional italic serif continuation line
 * @param {ReactNode?} props.railSub - Optional quiet supporting line under the
 *   rail headline (Story 4.6: the inquiry-contact confirmation page's "One tap
 *   and you're set. No account, no password."). Purely additive and omitted by
 *   default, so every existing caller renders byte-identically.
 * @param {Array<ReactNode>?} props.trustItems - Trust/context line items
 * @param {('photo'|'cream')?} props.backdrop - Stage backdrop. 'photo' (default)
 *   renders the homepage hero photo full-bleed under the maroon scrim (auth /
 *   recovery / reset behavior — unchanged). 'cream' skips the photo + scrim and
 *   lets the stage's deep warm cream base + radial maroon tint show as a calm
 *   stage (e.g. the Contact page).
 * @param {ReactNode} props.children - Body-side content
 * @returns {JSX.Element}
 */
const CrownAuthShell = props => {
  const {
    rootClassName,
    className,
    eyebrow,
    headline,
    headlineEm,
    railSub,
    trustItems,
    backdrop = 'photo',
    children,
  } = props;
  const isCream = backdrop === 'cream';
  const classes = classNames(rootClassName || css.stage, { [css.stageCream]: isCream }, className);

  return (
    <div className={classes}>
      {isCream ? null : (
        <>
          <div
            className={css.photo}
            style={{ backgroundImage: `url(${HERO_IMAGE})` }}
            aria-hidden="true"
          />
          <div className={css.scrim} aria-hidden="true" />
        </>
      )}
      <div className={css.crown}>
        <div className={css.rail}>
          <Logo layout="desktop" className={css.railLogo} alt="The Premier Horse" />
          <div className={css.railMid}>
            {eyebrow ? <div className={css.eyebrow}>{eyebrow}</div> : null}
            <h1 className={css.headline}>
              {headline}
              {headlineEm ? <em className={css.headlineEm}>{headlineEm}</em> : null}
            </h1>
            {railSub ? <p className={css.railSub}>{railSub}</p> : null}
          </div>
          <TrustListStacked items={trustItems} />
        </div>
        <div className={css.bodySide}>{children}</div>
        <TrustLineHorizontal items={trustItems} className={css.trustMobile} />
      </div>
    </div>
  );
};

export default CrownAuthShell;
